/**
 * PLAY SWITCHES (Tom 2026-09-27, job #496): Tom plays PLAY inside the command
 * surface, which has no address bar, so the `?param` test switches can't be typed
 * there. This registry names each one. `PlaySwitchesChip.vue` sets them per device
 * in localStorage, and `switchSearch()` folds them into the search string every
 * reader already parses. A param in the real URL still wins. On a copy that is not
 * PLAY, nothing is stored and nothing shows.
 *
 * A new switch is one line in SWITCHES. `value` is exactly what the URL param would
 * carry, and '' means leave it unset, which is the code's own default.
 */
export interface PlaySwitch {
  param: string
  label: string
  options: readonly { label: string; value: string }[]
}

export const SWITCHES: readonly PlaySwitch[] = [
  { param: 'typebox', label: 'Typing boxes', options: [{ label: 'auto', value: '' }, { label: 'boxes', value: '1' }, { label: 'plain', value: '0' }] },
  { param: 'wordaudio', label: 'Word audio', options: [{ label: 'default', value: '' }, { label: 'on', value: '1' }, { label: 'off', value: '0' }] },
  { param: 'speakinvite', label: 'Speak invite', options: [{ label: 'normal', value: '' }, { label: 'now', value: 'now' }, { label: 'always', value: 'always' }] },
  { param: 'speakcopy', label: 'Speak copy', options: [{ label: 'a', value: '' }, { label: 'b', value: 'b' }, { label: 'c', value: 'c' }] },
]

const KEY = 'ssi.playSwitches'

export const isPlayCopy = (): boolean =>
  import.meta.env.VITE_PLAY === '1' || String(import.meta.env.BASE_URL ?? '').startsWith('/play/')

export function readSwitches(): Record<string, string> {
  if (!isPlayCopy() || typeof localStorage === 'undefined') return {}
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}')
    return v && typeof v === 'object' ? v : {}
  } catch { return {} }
}

export function writeSwitch(param: string, value: string): void {
  if (!isPlayCopy()) return
  const all = readSwitches()
  if (value) all[param] = value
  else delete all[param]
  try { localStorage.setItem(KEY, JSON.stringify(all)) } catch { /* private mode: nothing kept */ }
}

/** The page's search string with this device's PLAY switches added where the URL sets none. */
export function switchSearch(search: string = typeof window !== 'undefined' ? window.location.search : ''): string {
  const q = new URLSearchParams(search)
  for (const [k, v] of Object.entries(readSwitches())) if (v && !q.has(k)) q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}
